import { getToken } from './auth.utils';
import { isObject } from './type.utils';
import type { AuthOptions } from './types/auth.types';
import { ApiError } from './types/error.types';
import { getAppLocale, getCountryLanguage, getCoworkerProfile, getOrdersCoworkerPreferences } from './window.utils';

export type FetcherOptions = AuthOptions;

export const getHeaders = async (headers?: HeadersInit, authOptions?: AuthOptions) => {
    if (authOptions?.skipAuth) {
        return new Headers(headers);
    }

    const token = await getToken(authOptions);
    if (!token) {
        throw new Error('No token found');
    }

    const { country } = getCountryLanguage();
    const coworkerProfile = getCoworkerProfile();
    const options = new Headers(headers);
    options.set('authorization', `Bearer ${token}`);
    options.set('x-countrycode', country.toUpperCase());
    options.set(
        'x-bucode',
        coworkerProfile?.data?.coworkerPreferences?.buCode ?? getOrdersCoworkerPreferences()?.buCode ?? ''
    );
    options.set('x-request-id', crypto.randomUUID());
    options.set('x-locale', getAppLocale());
    options.set('Accept-Language', getAppLocale());

    const externalCoworkerId = coworkerProfile?.data?.msalProfile?.onPremisesExtensionAttributes.extensionAttribute15;

    if (externalCoworkerId) {
        options.set('X-User-Id', externalCoworkerId);
        options.set('External-Client', 'true');
    }
    return options;
};

export const parseErrorFromResponse = (response: Response): Promise<unknown> => {
    return response.json().catch(() => {
        return;
    });
};

export const parseMessageFromResponse = (data: unknown, response: Response): string => {
    if (isObject(data)) {
        if ('message' in data && typeof data.message === 'string') {
            return data.message;
        }

        if ('errorMessage' in data && typeof data.errorMessage === 'string') {
            return data.errorMessage;
        }
    }

    if (response.status < 500) {
        return `Client error occurred with status ${response.status}`;
    }

    return `Server error occurred with status ${response.status}`;
};

export const fetcher = async (
    input: RequestInfo | URL,
    init?: RequestInit,
    options?: FetcherOptions
): Promise<Response> => {
    const { headers: initHeaders, ...rest } = init ?? {};
    const headers = await getHeaders(initHeaders, options);
    return fetch(input, {
        headers,
        ...rest,
    }).then(async (res: Response) => {
        if (!res.ok) {
            const data = await parseErrorFromResponse(res);
            const error = new ApiError(parseMessageFromResponse(data, res), res.status, data, {
                name: 'XHR Response',
                value: {
                    'errorData': data,
                    'statusText': res.statusText,
                    'status': res.status,
                    'x-request-id': headers.get('x-request-id'),
                    'x-countrycode': headers.get('x-countrycode'),
                    'x-locale': headers.get('x-locale'),
                    'x-bucode': headers.get('x-bucode'),
                },
            });
            throw error;
        }
        return res;
    });
};
